import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore, useDomainStore } from '@/hooks/useStores';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import './family-creation.css';

const FamilyCreationScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tCommon } = useTranslation('common');
  const { isAuthenticated } = useAuth();
  const uiStore = useUIStore();
  const domainStore = useDomainStore();
  const navigate = useNavigate();

  const [familyName, setFamilyName] = useState('');

  if (!isAuthenticated) {
    return (
      <div className="create-family-page__auth-message">
        <h2>{t('createFamily.signInRequired')}</h2>
      </div>
    );
  }

  const canCreateFamily = domainStore.user?.allowedToCreateFamily ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyName.trim()) {
      uiStore.setFamilyCreationError(t('createFamily.nameRequired'));
      return;
    }

    uiStore.setFamilyCreationState('creating');
    uiStore.setFamilyCreationError(null);

    try {
      const response = await domainStore.createFamily({
        name: familyName,
        slug: familyName.toLowerCase().replace(/\s+/g, '-')
      });

      if (response) {
        uiStore.setFamilyCreationState('success');

        setTimeout(() => {
          navigate(`/families/${response.id}/overview`);
        }, 2000);
      } else {
        throw new Error('No response from API');
      }
    } catch (err) {
      uiStore.setFamilyCreationState('error');
      uiStore.setFamilyCreationError(t('createFamily.createRestrictedError'));
      // eslint-disable-next-line no-console
      console.error('Family creation error:', err);
    }
  };

  const handleCancel = () => {
    setFamilyName('');
    uiStore.setFamilyCreationError(null);
    uiStore.setFamilyCreationState('idle');

    try {
      navigate(-1);
    } catch {
      navigate('/families');
    }
  };

  const isCreating = uiStore.familyCreationState === 'creating';

  return (
    <div className="create-family-page">
      <header className="create-family-page__header">
        <h1 className="create-family-page__title">{t('createFamily.title')}</h1>
        <p className="create-family-page__lede">
          {t('createFamily.lede')} <ScreenHelpDot />
        </p>
      </header>

      <div className="create-family-page__sections">
        {!canCreateFamily && (
          <p className="create-family-page__notice" role="status">
            {t('createFamily.restrictedNotice')}
          </p>
        )}

        <section className="create-family-page__section">
          <form onSubmit={handleSubmit} className="create-family-page__form">
            <div className="create-family-page__field">
              <label htmlFor="family-name" className="create-family-page__label">
                {t('createFamily.nameLabel')}
              </label>
              <input
                id="family-name"
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder={t('createFamily.namePlaceholder')}
                disabled={!canCreateFamily || isCreating}
                className="create-family-page__input"
                autoComplete="organization"
                required
              />
            </div>

            {uiStore.familyCreationError && (
              <div className="create-family-page__message create-family-page__message--error" role="alert">
                {uiStore.familyCreationError}
              </div>
            )}

            {uiStore.familyCreationState === 'success' && (
              <div className="create-family-page__message create-family-page__message--success" role="status">
                {t('createFamily.successRedirect')}
              </div>
            )}

            <div className="create-family-page__actions">
              <button type="button" onClick={handleCancel} disabled={isCreating} className="btn btn--ghost">
                {tCommon('cancel')}
              </button>
              <button
                type="submit"
                disabled={!canCreateFamily || !familyName.trim() || isCreating}
                className="btn btn--primary"
                title={!canCreateFamily ? t('createFamily.restrictedNotice') : undefined}
              >
                {isCreating ? t('createFamily.creating') : t('createFamily.createButton')}
              </button>
            </div>
          </form>
        </section>

        <section className="create-family-page__section">
          <h2 className="create-family-page__section-title">{t('createFamily.whatsNext')}</h2>
          <ul className="create-family-page__next-steps">
            <li className="create-family-page__next-step">
              <strong>{t('createFamily.nextAddMembers')}</strong> {t('createFamily.nextAddMembersBody')}
            </li>
            <li className="create-family-page__next-step">
              <strong>{t('createFamily.nextGedcom')}</strong> {t('createFamily.nextGedcomBody')}
            </li>
            <li className="create-family-page__next-step">
              <strong>{t('createFamily.nextInvite')}</strong> {t('createFamily.nextInviteBody')}
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
});

export default FamilyCreationScreen;
